import { Button, ErrorState, LoadingState, toast } from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { useState } from 'react';

import { fetchMe } from '../../../api/auth.js';
import { fetchUnitLead, updateUnitLead } from '../../../api/lead.js';
import { completeReminder, createReminder, listReminders, type ReminderItem } from '../../../api/reminders.js';
import { ApiError } from '../../../lib/api-client.js';
import { useSyncStore } from '../../../stores/sync-store.js';

import { HistoryDrawer } from './lead/history-drawer.js';
import { NotesField } from './lead/notes-field.js';
import { StatusPanel } from './lead/status-panel.js';
import { WorkEventsList } from './lead/work-events-list.js';

import type { UnitRow } from '../../../api/buildings.js';
import type { UnitLeadState } from '../../../api/lead.js';
import type { UpsertUnitLeadStatusInput } from '@sitewise/shared';


interface Props {
  unit: UnitRow;
}

/**
 * Migration 2 — Lead Tracker.
 *
 * Per-unit Lead tab. Replaces the M1 placeholder. Composes:
 *  - StatusPanel (2×2 grid of editable fields + derived overall pill)
 *  - WorkEventsList (structured "work done" log)
 *  - NotesField (free-form catch-all)
 *  - HistoryDrawer (opened from the "View change history" link)
 *
 * The page owns the save lifecycle: every child component dispatches
 * a partial patch through `handleSave`, which routes the call through
 * the sync indicator and updates the TanStack Query cache optimistically.
 */
export function LeadTab({ unit }: Props) {
  const queryClient = useQueryClient();
  const trackSync = useSyncStore((s) => s.track);
  const [historyOpen, setHistoryOpen] = useState(false);
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const stateQuery = useQuery({
    queryKey: ['unit-lead', unit.id],
    queryFn: () => fetchUnitLead(unit.id),
  });

  const saveMutation = useMutation({
    mutationFn: async (patch: UpsertUnitLeadStatusInput) =>
      trackSync(updateUnitLead(unit.id, patch)),
    onSuccess: (result) => {
      // Server returned the full new state — replace the cached
      // record so the derived pill, attachments, and timestamps all
      // update without a refetch.
      queryClient.setQueryData<UnitLeadState>(
        ['unit-lead', unit.id],
        (prev) => ({
          ...result.state,
          workEvents: prev?.workEvents ?? [],
        }),
      );
      // The activity tab + the unit detail header (which pulls
      // `unit.leadStatus` from `GET /units/:id`) should refresh so
      // the rollup pill matches.
      void queryClient.invalidateQueries({ queryKey: ['unit', unit.id] });
      void queryClient.invalidateQueries({ queryKey: ['unit', unit.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['unit-lead-history', unit.id] });
      void queryClient.invalidateQueries({ queryKey: ['building', result.state.buildingId, 'units'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not save lead status');
    },
  });

  const remindersQuery = useQuery({
    queryKey: ['reminders', { entityType: 'unit', entityId: unit.id }],
    queryFn: () => listReminders({ entityType: 'unit', entityId: unit.id, status: ['open'], limit: 20 }),
  });
  const canMutateReminders = (() => {
    const role = meQuery.data?.user.role;
    return role === 'admin' || role === 'building_manager' || role === 'maintenance';
  })();

  const addReminderMutation = useMutation({
    mutationFn: async () => {
      const due = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      return createReminder({
        title: `Retest lead for unit ${unit.unitNumber}`,
        description: 'Follow-up retest reminder from Lead tab',
        entityType: 'unit',
        entityId: unit.id,
        dueAt: due.toISOString(),
        priority: 'high',
        assigneeUserIds: [],
      });
    },
    onSuccess: () => {
      toast.success('Retest reminder created');
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
      void queryClient.invalidateQueries({ queryKey: ['reminders-summary'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not create reminder');
    },
  });

  const completeReminderMutation = useMutation({
    mutationFn: (id: string) => completeReminder(id, { done: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
      void queryClient.invalidateQueries({ queryKey: ['reminders-summary'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not complete reminder');
    },
  });

  async function handleSave(patch: UpsertUnitLeadStatusInput): Promise<void> {
    await saveMutation.mutateAsync(patch);
  }

  if (stateQuery.isLoading) return <LoadingState rows={6} />;
  if (stateQuery.isError || !stateQuery.data) {
    return <ErrorState description="Could not load this unit's lead status." />;
  }

  const state = stateQuery.data;

  return (
    <div className="space-y-4">
      <StatusPanel
        unitId={unit.id}
        state={state}
        onSave={handleSave}
        disabled={saveMutation.isPending}
      />
      <WorkEventsList
        unitId={unit.id}
        events={state.workEvents}
        disabled={saveMutation.isPending}
      />
      <NotesField
        notes={state.status.notes}
        disabled={saveMutation.isPending}
        onSave={handleSave}
      />
      <LeadRemindersCard
        reminders={remindersQuery.data?.reminders ?? []}
        loading={remindersQuery.isLoading}
        error={remindersQuery.isError}
        canMutate={canMutateReminders}
        onCreate={() => addReminderMutation.mutate()}
        onComplete={(id) => completeReminderMutation.mutate(id)}
        busy={addReminderMutation.isPending || completeReminderMutation.isPending}
      />

      <div className="pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-fg-muted"
          onClick={() => setHistoryOpen(true)}
        >
          <History className="h-3.5 w-3.5" />
          View change history
        </Button>
      </div>

      <HistoryDrawer
        unitId={unit.id}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </div>
  );
}

function LeadRemindersCard({
  reminders,
  loading,
  error,
  canMutate,
  onCreate,
  onComplete,
  busy,
}: {
  reminders: ReminderItem[];
  loading: boolean;
  error: boolean;
  canMutate: boolean;
  onCreate: () => void;
  onComplete: (id: string) => void;
  busy: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-surface p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Lead reminders</h3>
        {canMutate ? (
          <Button size="sm" variant="outline" onClick={onCreate} disabled={busy}>
            Set retest reminder (+30d)
          </Button>
        ) : (
          <span className="text-xs text-fg-muted">View-only</span>
        )}
      </div>
      {loading ? (
        <p className="text-xs text-fg-muted">Loading reminders…</p>
      ) : error ? (
        <p className="text-xs text-danger">Could not load reminders right now.</p>
      ) : reminders.length === 0 ? (
        <p className="text-xs text-fg-muted">No active reminders for this unit.</p>
      ) : (
        <ul className="space-y-2">
          {reminders.map((reminder) => (
            <li key={reminder.id} className="flex items-center justify-between gap-2 text-sm">
              <div>
                <div className="font-medium">{reminder.title}</div>
                <div className="text-xs text-fg-muted">
                  Due {new Date(reminder.dueAt).toLocaleDateString()}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onComplete(reminder.id)}
                disabled={busy || !canMutate || reminder.status !== 'open'}
              >
                Done
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
